/**
 * Light / dark toggle.
 *
 * The dark palette (`.theme-dark` in styles/globals.css) has existed since the
 * design system was written, but nothing ever applied the class — the app
 * shipped light-only. Every colour in the app already goes through the token
 * variables, so delivering dark mode is exactly this: a root class and a
 * remembered choice. No `dark:` class migration anywhere.
 *
 * `localStorage`, not `sessionStorage` like the auth store — a theme
 * preference should survive logging out and back in, same as the language
 * toggle (stores/i18n.ts).
 */
import { create } from 'zustand';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'pospak.theme';

function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('theme-dark', theme === 'dark');
}

interface ThemeState {
  theme: Theme;
  toggle: () => void;
}

export const useTheme = create<ThemeState>((set, get) => ({
  theme: readStoredTheme(),
  toggle() {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable — the choice lives for this tab only.
    }
    applyTheme(next);
    set({ theme: next });
  },
}));

// Applied at module load so the first paint is already the right theme —
// waiting for a component to mount would flash light for dark-mode users.
applyTheme(readStoredTheme());
